'use client';
import React, { useEffect, useState } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { Construction, Eye, EyeOff, HardHat, ArrowLeft } from 'lucide-react';
import { useT } from '@/components/I18nProvider';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { APP_NAME } from '@/lib/brand'

/**
 * Prisijungimas prie kranų parko valdymo.
 *
 * Du keliai sąmoningai skirtingi: administracija jungiasi el. paštu ir
 * slaptažodžiu, operatoriai — 6 skaitmenų kodu be slaptažodžio. Dalis operatorių
 * dirba su mygtukiniais telefonais ir slaptažodžio suvedimas jiems yra kliūtis,
 * todėl kodas paliktas ir čia.
 */

type Mode = 'choose' | 'staff' | 'operator';

export default function PrisijungimasPage() {
  const router = useRouter();
  const { t } = useT();
  const [mode, setMode] = useState<Mode>('choose');
  // Programėlė (Capacitor) VISADA atsidaro /prisijungimas. Jei darbuotojas jau
  // prisijungęs (galiojantis cookie), NErodom login formos — iškart nukreipiam į
  // jo ekraną. Kitaip atrodydavo, kad „atsijungė" ir reikia vėl vesti kodą.
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const session = await fetch('/api/auth/session').then((r) => r.json());
        const role = session?.user?.role;
        if (role && alive) {
          const { homeFor } = await import('@/lib/crane-nav');
          router.replace(role === 'WORKER' ? '/valandos' : homeFor(role));
          return;
        }
      } catch {
        /* nėra sesijos — rodom prisijungimą */
      }
      if (alive) setChecking(false);
    })();
    return () => { alive = false; };
  }, [router]);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
        <div className="text-sm text-slate-400">{t('Kraunama…')}</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-4 flex justify-center">
          <LanguageSwitcher />
        </div>
        <div className="mb-8 text-center">
          <div className="mb-3 flex justify-center">
            <div className="rounded-2xl bg-amber-500 p-3">
              <Construction className="text-white" size={32} />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">{APP_NAME}</h1>
          <p className="text-sm text-slate-500">{t('Kranų parko valdymas')}</p>
        </div>

        {mode === 'choose' && <Chooser onPick={setMode} />}
        {mode === 'staff' && (
          <StaffForm onBack={() => setMode('choose')} onDone={(href) => router.replace(href)} />
        )}
        {mode === 'operator' && (
          <OperatorForm onBack={() => setMode('choose')} onDone={(href) => router.replace(href)} />
        )}
      </div>
    </div>
  );
}

function Chooser({ onPick }: { onPick: (m: Mode) => void }) {
  const { t } = useT();
  return (
    <div className="space-y-3">
      <button
        onClick={() => onPick('operator')}
        className="flex w-full items-center gap-4 rounded-xl bg-white p-4 text-left shadow-sm ring-1 ring-slate-200 hover:ring-amber-400"
      >
        <div className="rounded-xl bg-amber-100 p-3">
          <HardHat className="text-amber-700" size={24} />
        </div>
        <div>
          <div className="font-semibold text-slate-900">{t('Operatorius')}</div>
          <div className="text-sm text-slate-500">{t('Prisijungimas 6 skaitmenų kodu')}</div>
        </div>
      </button>

      <button
        onClick={() => onPick('staff')}
        className="flex w-full items-center gap-4 rounded-xl bg-white p-4 text-left shadow-sm ring-1 ring-slate-200 hover:ring-slate-400"
      >
        <div className="rounded-xl bg-slate-100 p-3">
          <Construction className="text-slate-700" size={24} />
        </div>
        <div>
          <div className="font-semibold text-slate-900">{t('Vadovybė')}</div>
          <div className="text-sm text-slate-500">{t('El. paštas ir slaptažodis')}</div>
        </div>
      </button>
    </div>
  );
}

function StaffForm({ onBack, onDone }: { onBack: () => void; onDone: (href: string) => void }) {
  const { t } = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await signIn('credentials', {
        redirect: false,
        email: email.trim().toLowerCase(),
        password,
      });
      if (!res || res.error) {
        setError(t('Neteisingas el. paštas arba slaptažodis.'));
        return;
      }
      // Kur leisti, sprendžia rolė — direktorius ir administratorius mato
      // skirtingus meniu, todėl pradinis ekranas imamas iš sesijos.
      const session = await fetch('/api/auth/session').then((r) => r.json());
      const role = session?.user?.role;
      const { homeFor } = await import('@/lib/crane-nav');
      onDone(role === 'WORKER' ? '/valandos' : homeFor(role));
    } catch {
      setError(t('Prisijungimo klaida. Bandykite dar kartą.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft size={14} /> {t('Atgal')}
      </button>

      <label className="mb-3 block">
        <span className="mb-1 block text-xs font-medium uppercase text-slate-500">{t('El. paštas')}</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          className="w-full rounded-lg border border-slate-300 p-3"
          placeholder="vardas@imone.lt"
          required
        />
      </label>

      <label className="mb-4 block">
        <span className="mb-1 block text-xs font-medium uppercase text-slate-500">{t('Slaptažodis')}</span>
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="w-full rounded-lg border border-slate-300 p-3 pr-11"
            required
          />
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400"
            aria-label={show ? t('Slėpti slaptažodį') : t('Rodyti slaptažodį')}
          >
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </label>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-800" role="alert">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-lg bg-slate-900 p-3 font-semibold text-white disabled:opacity-50"
      >
        {loading ? t('Jungiamasi…') : t('Prisijungti')}
      </button>
    </form>
  );
}

function OperatorForm({ onBack, onDone }: { onBack: () => void; onDone: (href: string) => void }) {
  const { t } = useT();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError(t('Kodą sudaro 6 skaitmenys.'));
      return;
    }
    setLoading(true);
    try {
      const res = await signIn('credentials', { redirect: false, workerCode: code });
      if (!res || res.error) {
        setError(t('Tokio kodo nėra. Patikrinkite arba kreipkitės į administratorių.'));
        return;
      }
      // Tuo pačiu kodu jungiasi operatorius ir mechanikas — nukreipiam pagal rolę.
      const { getSession } = await import('next-auth/react');
      const session = await getSession();
      const role = session?.user?.role;
      onDone(role === 'MECHANIC' ? '/meistras' : '/valandos');
    } catch {
      setError(t('Prisijungimo klaida. Bandykite dar kartą.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft size={14} /> {t('Atgal')}
      </button>

      <label className="mb-4 block">
        <span className="mb-1 block text-xs font-medium uppercase text-slate-500">
          {t('Darbuotojo kodas')}
        </span>
        {/* Dideli skaitmenys ir skaitinė klaviatūra — vedama pirštinėtomis
            rankomis, dažnai lauke. */}
        <input
          inputMode="numeric"
          pattern="\d*"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          className="w-full rounded-lg border border-slate-300 p-4 text-center text-3xl tracking-[0.4em]"
          placeholder="••••••"
          autoFocus
        />
      </label>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-800" role="alert">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || code.length !== 6}
        className="w-full rounded-lg bg-amber-500 p-4 text-lg font-semibold text-white disabled:opacity-50"
      >
        {loading ? t('Jungiamasi…') : t('Prisijungti')}
      </button>
    </form>
  );
}
